// Phase HR-003 Wave 7 — Welfare audit (doc Study #37).
//
// HR / Admin / GM all use this. Tap a facility chip, rate 1-5, tap Save.
// Issue + photo + corrective action are optional. Action stays open until
// someone closes it with a notes line.

import { useMemo, useState } from 'react'
import {
  Alert, App, Button, DatePicker, Empty, Form, Input, List, Modal,
  Rate, Select, Space, Tag, Typography,
} from 'antd'
import { CheckCircleOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { welfareApi, type FacilityType } from '@/features/hr/welfareApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { useHrMobilePolish, type ThresholdResult } from '@/shared/styles/hrMobilePolish'

dayjs.extend(relativeTime)
const { Text } = Typography

const FACILITY_CHIPS: { code: FacilityType; label: string; icon: string }[] = [
  { code: 'CANTEEN',    label: 'Canteen',    icon: '🍽️' },
  { code: 'WATER',      label: 'Water',      icon: '💧' },
  { code: 'TOILET',     label: 'Toilet',     icon: '🚻' },
  { code: 'LOCKER',     label: 'Locker',     icon: '🔐' },
  { code: 'TRANSPORT',  label: 'Transport',  icon: '🚐' },
  { code: 'HOSTEL',     label: 'Hostel',     icon: '🏠' },
  { code: 'REST_AREA',  label: 'Rest area',  icon: '🛋️' },
  { code: 'WASHROOM',   label: 'Washroom',   icon: '🚿' },
  { code: 'FIRST_AID',  label: 'First aid',  icon: '🩹' },
  { code: 'OTHER',      label: 'Other',      icon: '📌' },
]

function ratingTone(r: number): ThresholdResult {
  if (r >= 4) return 'green'
  if (r >= 3) return 'amber'
  return 'red'
}

export default function WelfarePage() {
  useHrMobilePolish()
  const { plantId } = usePlantId()
  const qc = useQueryClient()
  const canCreate = usePermission('welfare.create')
  const canUpdate = usePermission('welfare.update')
  const [openCapture, setOpenCapture] = useState(false)
  const [facilityFilter, setFacilityFilter] = useState<FacilityType | 'ALL'>('ALL')
  const [openOnly, setOpenOnly] = useState(false)

  const { data: stats } = useQuery({
    queryKey: ['welfare-stats', plantId],
    queryFn: () => welfareApi.stats({ plant_id: plantId }),
    enabled: !!plantId,
  })

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['welfare-list', plantId, facilityFilter, openOnly],
    queryFn: () => welfareApi.list({
      plant_id: plantId,
      facility_type: facilityFilter === 'ALL' ? undefined : facilityFilter,
      open_only: openOnly,
      limit: 200,
    }),
    enabled: !!plantId,
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['welfare-list'] })
    qc.invalidateQueries({ queryKey: ['welfare-stats'] })
  }

  return (
    <div className="hr-mobile-root">
      <PageHeader
        title="Welfare Audit"
        subtitle="Doc §37 — Canteen / Water / Toilet / Transport / Hostel / Rest. HR + Admin + GM."
      />

      {/* KPI tiles */}
      <div style={{
        display: 'grid', gap: 8, marginBottom: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      }}>
        <KPITile variant="neutral" value={stats?.total ?? 0} label="Total" />
        <KPITile
          variant={stats?.avg_rating == null ? 'neutral'
                 : stats.avg_rating >= 4 ? 'green'
                 : stats.avg_rating >= 3 ? 'amber' : 'red'}
          value={stats?.avg_rating == null ? '—' : stats.avg_rating.toFixed(1)}
          label="Avg rating" />
        <KPITile
          variant={(stats?.poor_count ?? 0) === 0 ? 'green' : 'red'}
          value={stats?.poor_count ?? 0} label="Poor (≤2)" />
        <KPITile
          variant={(stats?.overdue_count ?? 0) > 0 ? 'red' : 'green'}
          value={stats?.overdue_count ?? 0} label="Overdue actions" />
      </div>

      {/* Filter chips */}
      <Space wrap style={{ marginBottom: 12 }}>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />}
            onClick={() => setOpenCapture(true)}>
            New audit
          </Button>
        )}
        <div className="hr-filter-chips">
          <div role="button"
            className={`hr-filter-chip${facilityFilter === 'ALL' ? ' hr-filter-chip--active' : ''}`}
            onClick={() => setFacilityFilter('ALL')}>All</div>
          {FACILITY_CHIPS.map((f) => (
            <div key={f.code} role="button"
              className={`hr-filter-chip${facilityFilter === f.code ? ' hr-filter-chip--active' : ''}`}
              onClick={() => setFacilityFilter(f.code)}>
              {f.icon} {f.label}
            </div>
          ))}
        </div>
        <Button size="small" type={openOnly ? 'primary' : 'default'}
          onClick={() => setOpenOnly((v) => !v)}>
          Open only
        </Button>
      </Space>

      {isLoading ? <Empty description="Loading…" image={Empty.PRESENTED_IMAGE_SIMPLE} />
       : rows.length === 0 ? <Empty description="No welfare audits yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
       : (
        <div style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        }}>
          {rows.map((r) => (
            <WelfareCard key={r.id} row={r} canUpdate={canUpdate} onClosed={refresh} />
          ))}
        </div>
      )}

      <CaptureModal open={openCapture}
        onClose={() => setOpenCapture(false)} onSaved={refresh} />
    </div>
  )
}

function WelfareCard({ row, canUpdate, onClosed }: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: any; canUpdate: boolean; onClosed: () => void
}) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [closing, setClosing] = useState(false)
  const [notes, setNotes] = useState('')

  const closeMut = useMutation({
    mutationFn: () => welfareApi.close(row.id, notes),
    onSuccess: () => {
      message.success('Closed.')
      qc.invalidateQueries({ queryKey: ['welfare-list'] })
      qc.invalidateQueries({ queryKey: ['welfare-stats'] })
      setClosing(false); setNotes(''); onClosed()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  const tone = ratingTone(row.condition_rating)
  const stripe = tone === 'red' ? '#cf1322' : tone === 'amber' ? '#d48806'
               : tone === 'green' ? '#389e0d' : undefined
  const facilityMeta = FACILITY_CHIPS.find(f => f.code === row.facility_type)
  const isClosed = !!row.action_closed_at

  return (
    <div className="hr-roster-card" style={{
      flexDirection: 'column', alignItems: 'stretch',
      borderLeft: stripe ? `4px solid ${stripe}` : undefined,
    }}>
      <Space wrap>
        <Tag>{facilityMeta?.icon} {row.facility_type}</Tag>
        <Tag color={tone === 'red' ? 'red' : tone === 'amber' ? 'gold'
                  : tone === 'green' ? 'green' : 'default'}>
          {row.condition_rating}/5
        </Tag>
        {row.location_code && <Tag>{row.location_code}</Tag>}
        {row.is_overdue && (
          <Tag color="red" icon={<WarningOutlined />}>Overdue</Tag>
        )}
        {isClosed && <Tag color="green">CLOSED</Tag>}
      </Space>
      {row.issue_observed && (
        <Text style={{ marginTop: 6 }}>{row.issue_observed}</Text>
      )}
      {row.photo_url && (
        <img src={row.photo_url} alt="evidence"
          style={{ maxWidth: '100%', maxHeight: 140, marginTop: 6,
                    borderRadius: 6, border: '1px solid #f0f0f0' }} />
      )}
      {row.action_taken && (
        <Text style={{ fontSize: 12, marginTop: 4, color: '#1677ff' }}>
          Action: {row.action_taken}
        </Text>
      )}
      <Text style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
        {dayjs(row.observation_date).format('DD MMM')}
        {row.reported_by_name && <> · by {row.reported_by_name}</>}
        {row.action_owner_name && <> · owner {row.action_owner_name}</>}
      </Text>
      {row.action_closure_notes && (
        <Text style={{ fontSize: 12, color: '#389e0d', marginTop: 4 }}>
          <CheckCircleOutlined /> {row.action_closure_notes}
        </Text>
      )}
      {canUpdate && !isClosed && (
        <div style={{ marginTop: 8 }}>
          {!closing ? (
            <Button size="small" type="primary" icon={<CheckCircleOutlined />}
              onClick={() => setClosing(true)}>Close action</Button>
          ) : (
            <Space.Compact style={{ width: '100%' }}>
              <Input placeholder="Closure note" value={notes}
                onChange={(e) => setNotes(e.target.value)} />
              <Button type="primary" loading={closeMut.isPending}
                disabled={!notes.trim()} onClick={() => closeMut.mutate()}>
                Save
              </Button>
              <Button onClick={() => { setClosing(false); setNotes('') }}>
                Cancel
              </Button>
            </Space.Compact>
          )}
        </div>
      )}
    </div>
  )
}

function CaptureModal({ open, onClose, onSaved }: {
  open: boolean; onClose: () => void; onSaved: () => void
}) {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [obsDate, setObsDate] = useState<Dayjs>(dayjs())
  const [facility, setFacility] = useState<FacilityType | null>(null)
  const [location, setLocation] = useState('')
  const [rating, setRating] = useState<number>(3)
  const [issue, setIssue] = useState('')
  const [action, setAction] = useState('')
  const [photoUrl, setPhotoUrl] = useState('')

  useMemo(() => {
    if (open) {
      setObsDate(dayjs()); setFacility(null); setLocation('')
      setRating(3); setIssue(''); setAction(''); setPhotoUrl('')
    }
  }, [open])

  const saveMut = useMutation({
    mutationFn: () => {
      if (!plantId) throw new Error('Plant missing')
      if (!facility) throw new Error('Pick a facility')
      return welfareApi.create({
        plant_id: plantId,
        observation_date: obsDate.format('YYYY-MM-DD'),
        facility_type: facility,
        condition_rating: rating,
        location_code: location || null,
        issue_observed: issue || null,
        action_taken: action || null,
        photo_url: photoUrl || null,
      })
    },
    onSuccess: () => {
      message.success('Welfare audit saved.')
      onSaved(); onClose()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(
      e?.message ?? e?.response?.data?.detail ?? 'Failed'),
  })

  return (
    <Modal open={open} onCancel={onClose}
      title="New welfare audit" width={520}
      onOk={() => saveMut.mutate()} confirmLoading={saveMut.isPending}
      okButtonProps={{ disabled: !facility }} okText="Save">
      <Form layout="vertical">
        <Space style={{ width: '100%' }}>
          <DatePicker value={obsDate} allowClear={false}
            onChange={(d) => d && setObsDate(d)} />
          <Input placeholder="Location e.g. Block A, GF"
            value={location} style={{ width: 230 }}
            onChange={(e) => setLocation(e.target.value)} />
        </Space>

        <div style={{ marginTop: 12 }}>
          <Text strong style={{ fontSize: 12 }}>Facility</Text>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
            {FACILITY_CHIPS.map((f) => (
              <button key={f.code} type="button"
                className={`hr-filter-chip${facility === f.code ? ' hr-filter-chip--active' : ''}`}
                onClick={() => setFacility(f.code)}>
                {f.icon} {f.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <Text strong style={{ fontSize: 12, marginRight: 8 }}>Condition</Text>
          <Rate value={rating} onChange={setRating} />
          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
            1 = unusable · 5 = excellent
          </Text>
        </div>

        <Form.Item label="Issue observed" style={{ marginTop: 12 }}>
          <Input.TextArea rows={2} value={issue}
            onChange={(e) => setIssue(e.target.value)}
            placeholder="What's wrong with the facility?" />
        </Form.Item>
        <Form.Item label="Action taken / proposed">
          <Input.TextArea rows={2} value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="What needs to be done" />
        </Form.Item>
        <Form.Item label="Photo URL (optional)">
          <Input value={photoUrl}
            onChange={(e) => setPhotoUrl(e.target.value)}
            placeholder="Paste photo URL after upload" />
        </Form.Item>
      </Form>
      <Alert showIcon
        message="Action stays open until someone closes it with a notes line — the doc rule about every observation ending in an action." />
    </Modal>
  )
}

function KPITile({ variant, value, label }: {
  variant: ThresholdResult; value: number | string; label: string
}) {
  return (
    <div className={`threshold-tile threshold-tile--${variant}`}>
      <div className="threshold-tile__value">{value}</div>
      <div className="threshold-tile__label">{label}</div>
    </div>
  )
}
